import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DeveloperMenu } from '../developer-menu';
import { Scenario } from '../../types';
import {
  addInlineChapter,
  addInlineMission,
  buildInlineStructure,
  findInlineSceneLocation,
  removeInlineChapter,
  updateChapterTitle,
  updateMissionTitle,
} from './inline-editor-model';
import { InlineChapterPanel } from './InlineChapterPanel';
import { InlineClipboardProvider } from './inline-clipboard-context';
import { PreviewModal } from './PreviewModal';
import { PreviewAssetLayerToggle } from './PreviewAssetLayerToggle';
import type { ScenarioAssetLayer } from '../../systems/scenario-asset-url';
import type { SortCategoryActions } from './sort-category-actions';
import {
  addCategoryFromGlobalToGame,
  addCategoryToGame,
  addGlobalCategory,
  bootstrapSituationSortCategories,
  removeGlobalCategory,
  setGameCategories,
  updateCategoryLabel,
  upsertGlobalCategory,
} from './situation-sort-categories';
import { uniqueId } from './utils';
import {
  isInlineEditorTextTarget,
  useInlineBlockHistory,
  type InlineScenarioChange,
} from './use-inline-block-history';
import type { OpenSceneInEditorHandler } from './inline-elements-model';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

interface InlineEditorProps {
  scenarioId: string;
  scenario: Scenario;
  onChange: (scenario: Scenario) => void;
  onOpenSceneInEditor?: OpenSceneInEditorHandler;
  onOpenSettings?: () => void;
  focusSceneId?: string | null;
  onFocusSceneConsumed?: () => void;
  /** When false, Ctrl+K scene navigation is handled by the parent (e.g. other studio tabs). */
  isActive?: boolean;
  previewAssetLayer?: ScenarioAssetLayer;
  onPreviewAssetLayerChange?: (layer: ScenarioAssetLayer) => void;
}

export const InlineEditor: React.FC<InlineEditorProps> = ({
  scenarioId,
  scenario,
  onChange,
  onOpenSceneInEditor,
  onOpenSettings,
  focusSceneId,
  onFocusSceneConsumed,
  isActive = true,
  previewAssetLayer = 'draft',
  onPreviewAssetLayerChange,
}) => {
  const [editMode, setEditMode] = useState(true);
  const [previewSceneId, setPreviewSceneId] = useState<string | null>(null);
  const [sceneNavOpen, setSceneNavOpen] = useState(false);
  const [localFocusSceneId, setLocalFocusSceneId] = useState<string | null>(null);
  const [currentSceneId, setCurrentSceneId] = useState<string | null>(null);
  const [collapsedMissions, setCollapsedMissions] = useState<Set<string>>(() => new Set());
  const [collapsedChapters, setCollapsedChapters] = useState<Set<string>>(() => new Set());
  const normalizedScenario = scenario;
  const structure = useMemo(() => buildInlineStructure(normalizedScenario), [normalizedScenario]);
  const characters = normalizedScenario.metadata.characters ?? [];
  const missions = normalizedScenario.metadata.missions ?? [];
  const missionTotal = missions.length || 1;
  const blockHistory = useInlineBlockHistory(scenarioId);
  const scenarioRef = useRef(normalizedScenario);
  scenarioRef.current = normalizedScenario;
  const bootstrappedScenarioIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (bootstrappedScenarioIdRef.current === scenarioId) return;
    bootstrappedScenarioIdRef.current = scenarioId;
    const bootstrapped = bootstrapSituationSortCategories(scenarioRef.current);
    if (bootstrapped !== scenarioRef.current) {
      onChange(bootstrapped);
    }
  }, [scenarioId, onChange]);

  const sortCategoryActions = useMemo<SortCategoryActions>(
    () => ({
      globalCategories: normalizedScenario.metadata.situationSortCategories ?? [],
      setGameCategories: (sceneId, categories) => {
        onChange(setGameCategories(scenarioRef.current, sceneId, categories));
      },
      addCategoryToGame: (sceneId, category) => {
        onChange(addCategoryToGame(scenarioRef.current, sceneId, category));
      },
      addFromGlobal: (sceneId, categoryId) => {
        onChange(addCategoryFromGlobalToGame(scenarioRef.current, sceneId, categoryId));
      },
      updateLabel: (sceneId, categoryId, label) => {
        onChange(updateCategoryLabel(scenarioRef.current, sceneId, categoryId, label));
      },
      addGlobalCategory: () => {
        const category = { id: uniqueId('cat'), label: 'Nouvelle catégorie' };
        onChange(addGlobalCategory(scenarioRef.current, category));
      },
      updateGlobalLabel: (categoryId, label) => {
        const existing = scenarioRef.current.metadata.situationSortCategories?.find(
          (category) => category.id === categoryId
        );
        if (!existing) return;
        onChange(upsertGlobalCategory(scenarioRef.current, { ...existing, label }));
      },
      removeGlobalCategory: (categoryId) => {
        const next = removeGlobalCategory(scenarioRef.current, categoryId);
        if (!next) {
          window.alert(
            'Cette catégorie est utilisée dans un jeu de tri et ne peut pas être supprimée.'
          );
          return;
        }
        onChange(next);
      },
    }),
    [normalizedScenario.metadata.situationSortCategories, onChange]
  );

  const applyScenarioChange: InlineScenarioChange = useCallback(
    (next, options) => {
      if (options?.recordBlockHistory) {
        blockHistory.pushSnapshot(scenarioRef.current);
      }
      onChange(next);
    },
    [blockHistory, onChange]
  );

  const { undo, redo } = blockHistory;

  const navigateToScene = useCallback(
    (sceneId: string) => {
      setSceneNavOpen(false);
      setCurrentSceneId(sceneId);

      const location = findInlineSceneLocation(scenario, sceneId);
      if (location) {
        setCollapsedMissions((prev) => {
          const next = new Set(prev);
          next.delete(location.missionKey);
          return next;
        });
        setCollapsedChapters((prev) => {
          const next = new Set(prev);
          next.delete(location.chapterKey);
          return next;
        });
      }

      setLocalFocusSceneId(sceneId);
    },
    [scenario]
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;

      const key = e.key.toLowerCase();
      if (key === 'k' && !e.altKey && isActive) {
        e.preventDefault();
        setSceneNavOpen((prev) => !prev);
        return;
      }

      if (isInlineEditorTextTarget(e.target)) return;

      if (key === 'z' && !e.shiftKey) {
        const restored = undo(scenarioRef.current);
        if (!restored) return;
        e.preventDefault();
        onChange(restored);
        return;
      }

      if (key === 'y' || (key === 'z' && e.shiftKey)) {
        const restored = redo(scenarioRef.current);
        if (!restored) return;
        e.preventDefault();
        onChange(restored);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, undo, redo, onChange]);

  const handleAddMission = () => {
    onChange(addInlineMission(scenario));
  };

  const handleAddChapter = (missionId: string) => {
    onChange(addInlineChapter(scenario, missionId));
  };

  const handleDeleteChapter = (chapterId: string, title: string) => {
    if (!window.confirm(`Supprimer le chapitre « ${title} » et tout son contenu ?`)) return;
    onChange(removeInlineChapter(scenario, chapterId));
    setCollapsedChapters((prev) => {
      const next = new Set(prev);
      next.delete(chapterId);
      return next;
    });
  };

  const toggleMissionCollapse = (id: string) => {
    setCollapsedMissions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleChapterCollapse = (id: string) => {
    setCollapsedChapters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const effectiveFocusSceneId = focusSceneId ?? localFocusSceneId;

  useLayoutEffect(() => {
    if (!effectiveFocusSceneId) return;

    const targetId = effectiveFocusSceneId;
    const fromParent = focusSceneId === targetId;

    if (fromParent) {
      const location = findInlineSceneLocation(scenario, targetId);
      if (location) {
        setCollapsedMissions((prev) => {
          const next = new Set(prev);
          next.delete(location.missionKey);
          return next;
        });
        setCollapsedChapters((prev) => {
          const next = new Set(prev);
          next.delete(location.chapterKey);
          return next;
        });
      }
      setCurrentSceneId(targetId);
    }

    let flashTimeout: number | undefined;
    let raf2: number | undefined;

    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById(`inline-scene-${targetId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add(inline.sceneFocusFlash);
          flashTimeout = window.setTimeout(() => {
            el.classList.remove(inline.sceneFocusFlash);
          }, 1500);
        }
        if (fromParent) {
          onFocusSceneConsumed?.();
        } else {
          setLocalFocusSceneId(null);
        }
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2 !== undefined) cancelAnimationFrame(raf2);
      if (flashTimeout !== undefined) window.clearTimeout(flashTimeout);
    };
  }, [effectiveFocusSceneId, focusSceneId, scenario, onFocusSceneConsumed]);

  return (
    <InlineClipboardProvider>
    <div className={inline.wrapper}>
      <div className={inline.toolbar}>
        <button
          type="button"
          className={`${studio.btnSecondary} ${editMode ? studio.tabActive : ''}`}
          onClick={() => setEditMode((v) => !v)}
        >
          {editMode ? 'Édition active' : 'Activer l\'édition'}
        </button>
        {editMode && (
          <button type="button" className={studio.btnSecondary} onClick={handleAddMission}>
            + Mission
          </button>
        )}
        {onPreviewAssetLayerChange && (
          <PreviewAssetLayerToggle
            value={previewAssetLayer}
            onChange={onPreviewAssetLayerChange}
          />
        )}
        <span className={inline.toolbarStatus}>
          {editMode ? 'Cliquez sur un texte pour le modifier' : 'Mode lecture'}
          {' · Ctrl+K'}
          {blockHistory.canUndo || blockHistory.canRedo
            ? ` · ${blockHistory.canUndo ? 'Ctrl+Z' : ''}${blockHistory.canUndo && blockHistory.canRedo ? ' / ' : ''}${blockHistory.canRedo ? 'Ctrl+Y' : ''}`
            : ''}
        </span>
      </div>

      <div className={inline.doc}>
        <header className={inline.docHeader}>
          <h2 className={inline.docTitle}>{normalizedScenario.metadata.title}</h2>
          <div className={inline.docSubtitle}>
            {missions.length ? `${missions.length} mission(s)` : ''}
            {normalizedScenario.metadata.chapters?.length
              ? `${missions.length ? ' · ' : ''}${normalizedScenario.metadata.chapters.length} chapitre(s)`
              : ''}
          </div>
        </header>

        {missions.length === 0 && normalizedScenario.scenes.length === 0 && (
          <p className={inline.emptyScene}>
            Aucune mission. {editMode ? 'Cliquez sur « + Mission » pour commencer.' : ''}
          </p>
        )}

        {structure.map((missionGroup, mi) => {
          const missionKey = missionGroup.mission?.id ?? `ungrouped-${mi}`;
          const missionCollapsed = collapsedMissions.has(missionKey);

          return (
          <section key={missionKey} className={inline.missionBlock}>
            {missionGroup.mission && (
              <div className={`${inline.sectionHeader} ${inline.sectionHeaderMission}`}>
                <button
                  type="button"
                  className={`${inline.collapseBtn} ${missionCollapsed ? inline.collapseBtnCollapsed : ''}`}
                  onClick={() => toggleMissionCollapse(missionKey)}
                  aria-expanded={!missionCollapsed}
                  aria-label={missionCollapsed ? 'Déplier la mission' : 'Replier la mission'}
                  title={missionCollapsed ? 'Déplier' : 'Replier'}
                >
                  ▾
                </button>
                <span className={inline.sectionLabel}>
                  Mission {missionGroup.mission.order}/{missionTotal}
                </span>
                {editMode ? (
                  <input
                    type="text"
                    className={inline.sectionTitleInput}
                    value={missionGroup.mission.title}
                    onChange={(e) =>
                      onChange(updateMissionTitle(scenario, missionGroup.mission!.id, e.target.value))
                    }
                    aria-label="Titre de la mission"
                  />
                ) : (
                  <span className={inline.sectionTitle}>{missionGroup.mission.title}</span>
                )}
                {editMode && (
                  <button
                    type="button"
                    className={`${studio.btnSecondary} ${inline.sectionAction}`}
                    onClick={() => handleAddChapter(missionGroup.mission!.id)}
                  >
                    + Chapitre
                  </button>
                )}
              </div>
            )}

            {!missionCollapsed && (
              <div className={inline.missionBody}>
                {missionGroup.mission && missionGroup.chapters.length === 0 && (
                  <p className={inline.emptyChapter}>
                    Aucun chapitre dans cette mission.
                    {editMode ? ' Ajoutez-en un avec « + Chapitre ».' : ''}
                  </p>
                )}

                {missionGroup.chapters.map((chapterGroup, ci) => {
                  const chapterKey = chapterGroup.chapter?.id ?? `${missionKey}-chapter-${ci}`;
                  const chapterCollapsed = collapsedChapters.has(chapterKey);

                  return (
              <div key={chapterKey} className={inline.chapterBlock}>
                {chapterGroup.chapter ? (
                  <div className={inline.sectionHeader}>
                    <button
                      type="button"
                      className={`${inline.collapseBtn} ${chapterCollapsed ? inline.collapseBtnCollapsed : ''}`}
                      onClick={() => toggleChapterCollapse(chapterKey)}
                      aria-expanded={!chapterCollapsed}
                      aria-label={chapterCollapsed ? 'Déplier le chapitre' : 'Replier le chapitre'}
                      title={chapterCollapsed ? 'Déplier' : 'Replier'}
                    >
                      ▾
                    </button>
                    <span className={inline.sectionLabel}>Chapitre</span>
                    {editMode ? (
                      <input
                        type="text"
                        className={inline.sectionTitleInput}
                        value={chapterGroup.chapter.title}
                        onChange={(e) =>
                          onChange(
                            updateChapterTitle(scenario, chapterGroup.chapter!.id, e.target.value)
                          )
                        }
                        aria-label="Titre du chapitre"
                      />
                    ) : (
                      <span className={inline.sectionTitle}>{chapterGroup.chapter.title}</span>
                    )}
                    <div className={inline.sectionHeaderActions}>
                      {chapterGroup.chapter.startScene && (
                        <span className={inline.sectionMeta}>
                          Début&nbsp;:
                          <span className={inline.sceneId}>{chapterGroup.chapter.startScene}</span>
                        </span>
                      )}
                      {editMode && (
                        <button
                          type="button"
                          className={studio.btnDanger}
                          onClick={() =>
                            handleDeleteChapter(chapterGroup.chapter!.id, chapterGroup.chapter!.title)
                          }
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  chapterGroup.scenes.length > 0 && (
                    <div className={inline.sectionHeader}>
                      <button
                        type="button"
                        className={`${inline.collapseBtn} ${chapterCollapsed ? inline.collapseBtnCollapsed : ''}`}
                        onClick={() => toggleChapterCollapse(chapterKey)}
                        aria-expanded={!chapterCollapsed}
                        aria-label={chapterCollapsed ? 'Déplier le script' : 'Replier le script'}
                        title={chapterCollapsed ? 'Déplier' : 'Replier'}
                      >
                        ▾
                      </button>
                      <span className={inline.sectionLabel}>Script</span>
                      <span className={inline.sectionTitle}>Hors chapitre</span>
                    </div>
                  )
                )}

                {!chapterCollapsed && (
                  <InlineChapterPanel
                    chapterId={chapterGroup.chapter?.id}
                    chapterScenes={chapterGroup.scenes}
                    scenarioId={scenarioId}
                    scenario={normalizedScenario}
                    editMode={editMode}
                    characters={characters}
                    sortCategoryActions={sortCategoryActions}
                    onScenarioChange={applyScenarioChange}
                    onOpenSceneInEditor={onOpenSceneInEditor}
                    onPreviewBlock={setPreviewSceneId}
                    onOpenSettings={onOpenSettings}
                  />
                )}
              </div>
                  );
                })}
              </div>
            )}
          </section>
          );
        })}
      </div>

      {previewSceneId && (
        <PreviewModal
          scenario={normalizedScenario}
          scenarioId={scenarioId}
          startSceneId={previewSceneId}
          assetLayer={previewAssetLayer}
          onClose={() => setPreviewSceneId(null)}
        />
      )}

      {sceneNavOpen && (
        <DeveloperMenu
          scenario={normalizedScenario}
          currentSceneId={currentSceneId ?? undefined}
          onNavigateToScene={navigateToScene}
          onClose={() => setSceneNavOpen(false)}
          title="Aller à la scène"
          subtitle="Naviguez dans l'éditeur inline · Ctrl+K"
          currentBadgeLabel="Actuelle"
        />
      )}
    </div>
    </InlineClipboardProvider>
  );
};
